/**
 * Form Class
 *
 * Should be used for all forms in application
 * Can validate, get data and make some styling for forms
 *
 * Example of usage:
 *
 * <form class="form fn-form">
 *     <div data-validation-type="name" class="fn-validate">
 *         <input type="email">
 *     </div>
 * </form>
 *
 * // Form constructor can take jBone element or CSS selector
 * var form = new Form($('.fn-form'), {
 *     // validation config for form, can take Object of Function
 *     validationConfig: {
 *         name: {
 *             email: true,
 *             length: {
 *                 min: 6,
 *                 max: 40,
 *                 message: 'Email length is invalid'
 *             }
 *         }
 *     }
 * });
 *
 * form
 *     .showLoading()
 *     .validate()
 *         .always(form.hideLoading.bind(form))
 *         .done(function(data, form) {
 *             console.log(form.data());
 *         })
 *         .fail(function(data) {
 *             console.error(data);
 *         });
 */

define(function(require, exports, module) {
    'use strict';

    var $ = require('j');
    var _ = require('underscore');
    var Class = require('Class');
    var i18n = require('i18n');
    var util = require('utils');
    var device = require('util/device.util');
    var tmpl = require('tmpl');
    var BaseView = require('common/base.view');
    var validation = require('util/validation.util');
    var domUtil = require('util/dom.util');
    var config = require('config');
    var DropdownEditable = require('common/dropdown.editable/dropdown.editable.view');
    var AddressLookupSwiss = require('common/addressLookupSwiss/addressLookupView');
    var AddressLookup = require('common/addressLookup/addressLookupView');

    var _extendSelectors = function(selectors, pseudo) {
        return selectors.join(pseudo + ', ') + pseudo;
    };

    var Form = Class.create({
        customControlsMap: {
            'dropdown-editable': DropdownEditable,
            'addressLookupSwiss': AddressLookupSwiss,
            'addressLookup': AddressLookup
        },

        /**
         * Create new Form instance
         * @param  {jBone|HTMLFormElement|String} form - jBone element of CSS selector
         * @param  {Object} [options] - config for Form instance
         */
        initialize: function(form, options) {
            if (!form || !form.length && !(form instanceof window.HTMLFormElement)) {
                throw new Error('Form element is required.');
            }

            if (!(form instanceof $)) {
                form = $(form);
            }

            this.options = util.deepExtend({}, {
                id: _.uniqueId('form'),
                validationConfig: {},
                mutators: []
            }, options);

            this.isValid = true;
            this._customControls = [];
            this.hightlightControlClass = 'fn-highlight-control';
            this.validationInProgressFields = [];

            if (options && _.isObject(options.validationConfig)) {
                this.setValidationConfig(options.validationConfig);
            }

            if (options && 'data' in options) {
                _.extend(this.options, {
                    data: options.data
                });
            }

            Object.defineProperty(this, '$elements', {
                configurable: true,
                get: function() {
                    return this.$el.find('.fn-validate');
                }
            });

            this.setElement(form);
        },

        /**
         * Set form element for instance and delegate events
         * @param {jBone} $el
         * @return {Form}
         */
        setElement: function($el) {
            this.el = $el[0];
            this.$el = $el;

            this.renderCustomControls();
            this.delegateEvents();
            this.detectControlsType();
            this.wrapFormControls();

            this.selectedData = this.data();

            return this;
        },

        /**
         * Add new field to existing form
         * @param {jBone} $el
         * return {Form}
         */
        addField: function($el) {
            this.wrapFormControl($el.find('select, input')[0]);
            this.delegateEvents();
        },

        /**
         * Set focus on last control with "autofocus" attribute
         * @private
         */
        _setAutoFocus: function() {
            var el = _.last(this.$el.find('[autofocus]'));

            if (device.formFactor === 'desktop') {
                el && setTimeout(el.focus.bind(el), 100);
            }
        },

        /**
         * Delegate all change/validate events for inputs of current form
         * @return {Form}
         */
        delegateEvents: function() {
            var isEventTriggeredOnFormField = function(e) {
                return Array.from(this.$elements).some(function(el) {
                    var isInField = el.contains(e.target);

                    if (isInField) {
                        var error = el.querySelector('.error-tooltip');

                        isInField = !error || !error.contains(e.target);
                    }

                    return isInField;
                });
            }.bind(this);
            var getFormControlFromEvent = function(e) {
                return Array.from(this.$elements).filter(function(el) {
                    return el.contains(e.target);
                })[0];
            }.bind(this);

            this._setAutoFocus();

            if (!domUtil.isPlaceholderSupported) {
                domUtil.setCustomPlaceholder(this.$el.find('input'));
            }

            // fix dropDown list after device rotation on SGT3
            $(window).on('resize.form', _.throttle(function() {
                if (!document.activeElement || !document.activeElement.tagName) {
                    return;
                }

                var isSelect = document.activeElement.tagName.toLowerCase() === 'select';
                var isSGT3 = device.alias === 'galaxy_tab_3_10';
                var isChrome = device.browserName === 'chrome';

                if (isSelect && isSGT3 && isChrome) {
                    document.activeElement.blur();
                }
            }, 500));

            // fix for iOS 10
            if (device.os === 'ios' && parseInt(device.osVersion) === 10) {
                this.$el.on('submit.form', BaseView.hideKeyboard);
            }

            this.$elements.find('select, input[type="checkbox"], input[type="radio"].segmented-radio')
                .on('change.form', function(e) {
                    BaseView.redraw(e.target);
                });

            var tapEvent = 'tap.form' + this.options.id;
            var focusInEvent = 'focusin.form' + this.options.id;
            var validationEvent;

            $('body')
                .on(tapEvent + ' ' + focusInEvent, function(event) {
                    var isSameField = false;
                    var isFormFieldEvent = isEventTriggeredOnFormField(event);
                    var $activeFormField = isFormFieldEvent
                        ? $(getFormControlFromEvent(event))
                        : null;
                    var isEnabledFiled = !event.target.disabled;
                    const { target: { type } } = event;

                    if (event.target.readOnly && (type === 'radio' || type === 'checkbox')) {
                        event.preventDefault();

                        return;
                    }

                    if (isFormFieldEvent && event.type === 'focusin') {
                        $activeFormField.trigger('form:focusin.form');
                    }

                    if ($activeFormField && isEnabledFiled) {
                        this.resetField($activeFormField);
                    }

                    if (validationEvent) {
                        isSameField = $activeFormField && $activeFormField[0].contains(validationEvent.target);

                        if (!isSameField) {
                            this.validateField(getFormControlFromEvent(validationEvent), this.options.data, {
                                event: validationEvent
                            });
                        }
                        validationEvent = null;
                    }
                }.bind(this))
                .on('focusout.form' + this.options.id, function(event) {
                    if (isEventTriggeredOnFormField(event)) {
                        $(event.target).trigger('form:focusout.form');
                        validationEvent = event;
                    }
                })
                .on('keydown.form' + this.options.id, function(event) {
                    var { key, target } = event;
                    var spaceKey = key === ' ' || key === 'Spacebar';

                    if (spaceKey && target.className.includes(this.hightlightControlClass)) {
                        event.preventDefault();
                        event.stopImmediatePropagation();

                        var inputElement = target.querySelector('input') || {};
                        var { type, checked } = inputElement || {};

                        if (type !== 'radio' && type !== 'checkbox' || checked && type === 'radio') {
                            return;
                        }

                        inputElement.checked = !checked;
                        $(inputElement).trigger('change');
                    }

                    if (key === 'Enter' && target.className.includes('fn-toggle-password-visibility')) {
                        event.stopImmediatePropagation();

                        this.handleTogglePasswordVisibility(event);
                    }
                }.bind(this))
                .on('mousedown.form' + this.options.id, (event) => {
                    if (event.target.readOnly) {
                        event.preventDefault();
                    }
                });

            var eventName = device.formFactor === 'desktop' ? 'mousedown.form' : 'touchstart.form';

            this.$el
                .on(eventName, '.fn-toggle-password-visibility', this.handleTogglePasswordVisibility.bind(this));

            return this;
        },

        /**
         * Remove all event handlers from form
         * @return {Form}
         */
        undelegateEvents: function() {
            this.$elements.find('input, select, textarea').off('.form');
            this.$elements.off('.form');
            this.$wrappedElements && this.$wrappedElements.off('.form');
            this.$el.off('.form');
            $('body').off('.form' + this.options.id);
            $(window).off('.form');

            this._customControls.forEach(function(control) {
                control.close();
            });

            return this;
        },

        /**
         * Set additional class for control wrapper
         * @return {Form}
         */
        detectControlsType: function() {
            this.$elements.forEach(function(item) {
                var $item = $(item);
                var control = $item.find('input, select, textarea')[0];
                var clsMap = {
                    'select-one': 'select',
                    'hidden': ''
                };
                var cls = control.type in clsMap
                    ? clsMap[control.type]
                    : control.type;

                $item.addClass(cls);
            });

            return this;
        },

        /**
         * Method returns array of inputs of current form
         * @param  {Node} form          current form
         * @param  {Boolean} acceptAll  include all inputs in output
         * @return {Array}              array with inputs values
         */
        serializeToArray: function(form, acceptAll) {
            var result = [];

            _.toArray(this.el.elements)
                .filter(function(el) {
                    if (el.nodeName.toLowerCase() === 'fieldset') {
                        return false;
                    }

                    if (el.disabled && !acceptAll) {
                        return false;
                    }

                    var type = el.type;

                    if (type === 'submit' || type === 'reset' || type === 'button') {
                        return false;
                    }

                    // filter out unchecked radio buttons and checkboxes
                    // this, in particular, handles getting radio-button group value
                    if ((type === 'radio' || type === 'checkbox') && !el.checked) {
                        return false;
                    }

                    return Boolean(el.name);
                })
                .forEach(function(el) {
                    result.push({
                        name: el.name,
                        value: domUtil.getInputValue(el)
                    });
                });

            return result;
        },

        /**
         * Set validation config for form instance
         * @param {Object} validationCfg
         */
        setValidationConfig: function(validationCfg) {
            this.options.validationConfig = validationCfg;
        },

        validateField: function(field, data, options) {
            options = options || {};

            var dfd = $.Deferred();

            dfd = validation.validateDomField($(field), {
                validationConfig: this.options.validationConfig,
                form: this,
                dfd: dfd,
                event: options.event,
                nextField: options.nextField,
                data: data
            });

            dfd.fail(function(validationData) {
                this.setFieldInvalid($(field), validationData);
            }.bind(this)).done(function(validationData) {
                this.setFieldValid($(field), validationData);
            }.bind(this));

            return dfd.promise();
        },

        /**
         * Validate form, all inputs or by selector
         * @param  {String} [selector] jBone CSS selector
         * @param  {Object} [data] custom data for validation
         * @param  {Object} [options] additional fields for validation e.g. event
         * @param  {Boolean} [isSilent] is needed to update fields value state
         * @return {Deferred}
         */
        validate: function(selector, data, options, isSilent) {
            var process = $.Deferred();

            if (this.validationInProgressFields.length) {
                return process.reject();
            }

            var elements = selector ? $(selector) : this.$elements;
            elements.find('.error-tooltip').remove();

            this.hideErrorMessages();
            var validations = elements.map(function(field) {
                return this.validateField(field, data, options);
            }, this);

            $.when.apply($, validations)
                .fail(function() {
                    this.isValid = false;
                    process.reject({
                        ok: false,
                        message: 'Data is invalid'
                    });
                }.bind(this))
                .done(function() {
                    if (!isSilent) {
                        this.selectedData = this.data();
                    }

                    process.resolve(this.selectedData, this);
                }.bind(this));

            return process;
        },

        /**
         * Update fields value state
         */
        updateFormData: function() {
            this.selectedData = this.data();
        },

        /**
         * Check if form data was changed
         * @return {Boolean}
         */
        isDataChanged: function() {
            return !_.isEqual(this.selectedData, this.data());
        },

        /**
         * Return object of changed fields in format {field name: value, ...}
         * @return {Object}
         */
        getChangedData: function() {
            return _.reduce(this.data(), function(memory, value, key) {
                if (value !== this.selectedData[key]) {
                    memory[key] = value;
                }
                return memory;
            }.bind(this), {});
        },

        /**
         * Fills Form with data given or return form data
         * if calls without arguments
         *
         * @param {string|Object} [data] - input name or object with key:value
         * @param {*} [value] - input value to be set
         * @param {Array} [mutators] - data mutators
         * @return {Object}
         */
        data: function(data, value, mutators) {
            if (data === false || typeof data === 'object') {
                return this._fillOrClearForm(data, value);
            }
            if (arguments.length === 2) {
                return this._fillOneField(data, value);
            }
            if (data === undefined) {
                return this._getAllData(mutators);
            }
            if (typeof data === 'string') {
                return this._getValueOfOneField(data, mutators);
            }
        },

        _changeValue: function(el, elValue) {
            var type = el.getAttribute('type');
            var node = el.nodeName.toLocaleLowerCase();
            var $inputContainer;

            if (type === 'checkbox') {
                el.checked = elValue === 'on';
            } else if (type === 'radio') {
                if (elValue) {
                    el.checked = true;
                    el.value = elValue;
                } else {
                    el.checked = false;
                }
            } else if (node === 'select') {
                if (elValue) {
                    elValue = String(elValue);

                    [].forEach.call(el.children, function(option) {
                        option.removeAttribute('selected');
                        option.selected = false;

                        if (option.value === elValue) {
                            el.value = elValue;
                        }
                    });
                } else {
                    [].forEach.call(el.children, function(option) {
                        option.removeAttribute('selected');
                        option.selected = false;
                    });

                    el.children[0].setAttribute('selected', 'selected');
                    el.value = el.children[0].value;
                }

                $(el).trigger('input');
            } else {
                el.value = elValue || '';
                $(el).trigger('input');
            }

            if (!elValue) {
                let $fieldsetContainer = $(el).parents('.fieldset');
                $inputContainer = $(el).parents('.field');
                this.removeErrorTooltip($inputContainer);

                $inputContainer.removeClass('valid invalid');
                $fieldsetContainer.removeClass('valid invalid');
            }
        },

        _fillOrClearForm: function(data, value) {
            var fields;

            if (value) {
                fields = this.$el.find(value);
            } else {
                fields = _.toArray(this.el.elements);
            }

            fields.forEach(function(el) {
                var type = el.getAttribute('type');

                if (el.nodeName.toLowerCase() !== 'fieldset' && !el.disabled &&
                    type !== 'submit' && type !== 'reset' && type !== 'button' &&
                    el.getAttribute('name')) {

                    if (data[el.name]) {
                        if (type !== 'radio' || el.value === data[el.name]) {
                            this._changeValue(el, data[el.name]);
                        } else {
                            el.checked = false;
                        }
                    } else if (data === false) {
                        this._changeValue(el, false);
                    }
                }
            }.bind(this));

            return this;
        },

        _fillOneField: function(fieldName, value) {
            var el = this.el.elements[fieldName];

            if (el.length > 1 && !el.options) {
                [].forEach.call(el, function(radio) {
                    radio.checked = false;
                    if (radio.value === value) {
                        el = radio;
                    }
                });
            }

            this._changeValue(el, value);

            return this;
        },

        _getAllData: function(mutators) {
            var formData = {};

            this.serializeToArray(this.el, false).forEach(function(field) {
                formData[field.name] = field.value;
            });

            return this._mutate(formData, mutators);
        },

        _getValueOfOneField: function(fieldName, mutators) {
            var fieldEl = this.el.elements[fieldName];
            var inputValue, result;

            if (fieldEl && fieldEl.length > 1 && fieldEl[0].type === 'radio') {
                fieldEl = _.find(fieldEl, function(el) {
                    return el.checked;
                });
            }

            if (fieldEl) {
                inputValue = domUtil.getInputValue(fieldEl);
            }

            result = this._mutate(_.object([fieldName], [inputValue]), mutators);

            return result[fieldName];
        },

        /**
         * Clear inputs values
         * @param  {String} [selector]  CSS selector for inputs
         * @return {Form}
         */
        clear: function(selector) {
            this.data(false, selector);

            return this;
        },

        /**
         * Mutates form data
         * @param  {Object} formData
         * @param  {Array} [mutators]
         * @return {Form}
         */
        _mutate: function(formData, mutators) {
            mutators = mutators || this.options.mutators;

            if (_.isEmpty(mutators)) {
                return formData;
            }

            return _.reduce(mutators, function(currentState, mutator) {
                return mutator.mutate(currentState);
            }, formData);
        },

        /**
         * Mark selected field like invalid
         * @param  {jBone}  $field
         * @param  {Object} data
         * @return {Form}
         */
        setFieldInvalid: function($field, data) {
            this.showErrorTooltip($field, data);
            $field.forEach(function(el) {
                el.classList.remove('valid');
                el.classList.add('invalid');
            });

            return this;
        },

        /**
         * Mark selected field like valid
         * @param  {jBone}  $field
         * @return {Form}
         */
        setFieldValid: function($field) {
            $field.forEach(function(el) {
                el.classList.remove('invalid');
                el.classList.add('valid');
            });
            this.removeErrorTooltip($field);

            return this;
        },

        /**
         * Reset selected field to default state
         * @param {jBone}  $field
         * @return {Form}
         */
        resetField: function($field) {
            $field.forEach(function(el) {
                el.classList.remove('valid');
                el.classList.remove('invalid');
            });

            this.removeErrorTooltip($field);

            return this;
        },

        /**
         * Show error tooltip for selected field
         * @param  {jBone}  $field
         * @param  {Object} data
         * @return {Form}
         */
        showErrorTooltip: function($field, data) {
            var $errorTooltip = $field.find('.error-tooltip');
            var message = i18n.t(data.dynamicMessage || data.message);

            if ($errorTooltip.length === 0) {
                $field.append('<div class="error-tooltip">' + message + '</div>');
            } else {
                $errorTooltip.html(message);
            }

            return this;
        },

        /**
         * Hide error tooltip for selected field
         * @param  {jBone}  $field
         * @return {Form}
         */
        removeErrorTooltip: function($field) {
            $field.find('.error-tooltip').remove();

            return this;
        },

        /*
         * @deprecated use `showErrorMessage`
         */
        showErrorMessages: function() {
            return Form.prototype.showErrorMessage.apply(this, arguments);
        },

        /**
         * Show message about error in form
         * @param  {Object} message Message structure
         * @return {Form}
         */
        showErrorMessage: function(message = {}) {
            this.$el.find('.fn-form-messages').html(tmpl['common.messages']({
                className: message.className || 'error',
                content: i18n.tExt(message.translation, message.content) || i18n.tExt('playtech.mobile.system.systemError', 'System error')
            }));

            return this;
        },

        /**
         * Hide error messages in form
         * @return {Form}
         */
        hideErrorMessages: function() {
            this.$el.find('.fn-form-messages').html('');

            return this;
        },

        /**
         * Show warning about error in form
         * @param {Object} message Message structure
         * @return {Form}
         */
        showWarningMessage: function(message) {
            this.$el.find('.fn-form-warning').html(tmpl['common.messages']({
                className: message.className || 'warning',
                content: i18n.tExt(message.translation, message.content)
            }));

            return this;
        },

        /**
         * Hide warning messages in form
         * @return {Form}
         */
        hideWarningMessages: function() {
            this.$el.find('.fn-form-warning').html('');

            return this;
        },

        /**
         * Disable all or selected by selector form inputs and show loading
         * @param  {String} [selector] jBone CSS selector
         * @return {Form}
         */
        showLoading: function(selector) {
            return this._setLoadingState(selector, true);
        },

        /**
         * Hide form loading
         * @param  {String} [selector] jBone CSS selector
         * @return {Form}
         */
        hideLoading: function(selector) {
            return this._setLoadingState(selector, false);
        },

        /**
         * Show/hide form loading state
         * @param [selector] - Parent element's jBone CSS selector
         * @param {Boolean} state - true = loading, false = not loading
         * @return {Form}
         * @private
         */
        _setLoadingState: function(selector, state) {
            var $parent = selector && (_.isString(selector) || selector instanceof $) ? selector : this.$el;

            this.loading = state;
            Form.toggleLoading($parent, state);

            return this;
        },

        /**
         * Wrap form controls for CSS styling
         * @param  {HTMLElement} [container] Container for form controls that will be styled
         * @return {Form}
         */
        wrapFormControls: function(container) {
            container = container ? $(container) : this.$el;

            container
                .find('select:not([multiple]):not([data-wrap-control="false"]),'
                    + ' input[type=checkbox], input[type=radio]:not(.segmented-radio)')
                .forEach(function(element) {
                    this.wrapFormControl(element);
                }, this);

            // IE & Edge browser have native password spy icon before version Windows v.10
            if (config.get('isEnablePasswordSpy')) {
                container
                    .find('input[type=password]')
                    .forEach(this.addPasswordVisibilityControl.bind(this));
            }

            return this;
        },

        /**
         * Wrap form control for CSS styling
         * @param  {HTMLElement} element    form control
         * @return {Form}
         */
        wrapFormControl: function(element) {
            var parent = element.parentNode,
                type = element.type === 'select-one' ? 'select' : element.type,
                wrapper = document.createElement('span'),
                display = document.createElement('span'),
                wrapperClassName = 'js-' + type,
                displayClassName = 'js-' + type + '__display';

            this.$wrappedElements = $(element);

            if (element.parentNode.className === wrapperClassName) {
                return this;
            }

            wrapper.className = wrapperClassName;
            display.className = displayClassName;

            parent.insertBefore(wrapper, element);
            wrapper.appendChild(element);
            wrapper.appendChild(display);

            if (type === 'radio' || type === 'checkbox') {
                element.tabIndex = -1;
                parent.tabIndex = element.readOnly || element.disabled ? -1 : 0;
                parent.classList.add(this.hightlightControlClass);
                parent.classList.add('fn-accessibility-element');
            }

            if (type === 'select' && element.options.length) {
                if (element.options.selectedIndex === 0 || element.options.selectedIndex === -1) {
                    display.textContent = element.options[0].text;

                    if (!element.options[0].value) {
                        element.classList.add('default');
                    }
                } else {
                    display.textContent = element.options[element.options.selectedIndex].text;
                }

                [].forEach.call(element.options, function(option) {
                    if (!option.value) {
                        option.disabled = true;
                        option.style.display = 'none';
                    }
                });

                this.$wrappedElements.on('change.form input.form',
                    Form.refreshSelectField.bind(null, element, display));
            }

            return this;
        },

        renderCustomControls: function() {
            this.$elements.forEach(function(el) {
                if (el.dataset.customControl in this.customControlsMap) {
                    var CustomControl = this.customControlsMap[el.dataset.customControl];
                    var customControlData = JSON.parse(decodeURIComponent(el.dataset.customControlData || '{}'));
                    var control = new CustomControl(_.extend(customControlData, { el: el, form: this }));
                    control.render();
                    this._customControls.push(control);
                }
            }.bind(this));
        },

        /**
         * @deprecated use dom.util::setCustomPlaceholder() instead
         */
        setCustomPlaceholder: function() {
            return domUtil._updateCustomPlaceholder.apply(this, arguments);
        },

        /**
         * @param {HTMLInputElement} passwordInputEl
         **/
        addPasswordVisibilityControl: function(passwordInputEl) {
            $(passwordInputEl)
                .addClass('fn-input-type-password')
                .parent()
                .append('<div tabindex="0" class="password-visibility fn-toggle-password-visibility fn-accessibility-element">'
                    + i18n.t('playtech.mobile.system.input.password.aria-label.show') + '</div>');
        },

        /**
         * Toggle password input element text visibility
         *
         * @param {jBone.Event} e
         **/
        handleTogglePasswordVisibility: function(e) {
            e.stopPropagation();
            e.preventDefault();

            var $passwordVisisbilityControl = $(e.target);
            var $inputPassword = $passwordVisisbilityControl.parent().find('.fn-input-type-password');

            $passwordVisisbilityControl.toggleClass('password-visibility--enabled');
            $inputPassword.attr('type', $inputPassword.attr('type') === 'password' ? 'text' : 'password');
            $passwordVisisbilityControl.attr('aria-live', 'polite');
            $passwordVisisbilityControl.html($inputPassword.attr('type') === 'password'
                ? i18n.t('playtech.mobile.system.input.password.aria-label.show')
                : i18n.t('playtech.mobile.system.input.password.aria-label.hide'));
        },

        /**
         * Hides field from form and disables input to prevent validation and passing value to form submission
         * @param {String} fieldName
         * @param {Boolean} isVisible
         */
        toggleFieldVisibility: function(fieldName, isVisible) {
            Form.toggleFieldVisibility(fieldName, isVisible, this.$el);
        },

        addValidationInProgressFields: function(fields = []) {
            this.validationInProgressFields = [...this.validationInProgressFields, ...fields];
        },

        /**
         * Removes fields from validationInProgressFields array which is needed to prevent form validation if some field validation is in progress yet
         * @param {Array<String>} [fields] - if not passed or empty, validationInProgressFields will be cleared
         */
        removeValidationInProgressFields: function(fields = []) {
            this.validationInProgressFields = fields.length
                ? this.validationInProgressFields.filter(fieldName => !fields.includes(fieldName))
                : [];
        }
    });

    Form.toggleLoading = function(selector, state) {
        var $el = selector instanceof $ ? selector : $(selector);
        var pseudo = state ? ':not([disabled])' : '.progress:disabled';
        var elSelector = _extendSelectors(['input', 'select', 'button', 'textarea'], pseudo);

        if (state) {
            $el.find(elSelector).addClass('progress').attr('disabled', '');
        } else {
            $el.find(elSelector).removeClass('progress').removeAttr('disabled');
        }
    };

    /**
     * Force refresh 'select' field display value
     * @param {HTMLElement|jBone} sourceEl - source 'select' element(s) to refresh
     * @param {HTMLElement|jBone|Array} [displayEl] - source's display element
     */
    Form.refreshSelectField = function(sourceEl, displayEl) {

        function processEl(srcEl, dispEl) {
            if (!srcEl || !srcEl.options) {
                return;
            }

            dispEl = dispEl || srcEl.parentNode.querySelector('.js-select__display');

            if (dispEl) {
                var selectedEl = srcEl.options[srcEl.options.selectedIndex];

                srcEl.classList.toggle('default', !(selectedEl && selectedEl.value));

                if (selectedEl) {
                    dispEl.textContent = dispEl.innerText = selectedEl.text;
                } else {
                    dispEl.textContent = dispEl.innerText = srcEl.options[0].text;
                }
            }
        }

        if (sourceEl instanceof $) {
            displayEl = displayEl instanceof $ || displayEl instanceof Array ? displayEl : null;
            sourceEl.forEach(function(el, index) {
                processEl(el, displayEl && displayEl[index]);
            });
        } else {
            processEl(sourceEl, displayEl);
        }

        return sourceEl;
    };

    /**
     * Hides field from form and disables input to prevent validation and passing value to form submission
     * @param {String} fieldName
     * @param {Boolean} isVisible
     * @param {jBone|Node} el - node to search fields from
     */
    Form.toggleFieldVisibility = function(fieldName, isVisible, el) {
        var $el = $(el);
        var $field = $el.find('.fieldset_name_' + fieldName + ',.field_name_' + fieldName);
        var $input = $field.find('input, select');
        var $fieldControls = $el.find('.address-lookup-action-wrapper_' + fieldName);
        var $innerFieldsContainer = $el.find('.address-lookup-inner-fields-container_' + fieldName);

        var $allInnerFieldsInputs = $innerFieldsContainer.find('input, select');
        var $notDisabledInnerFieldsInputs = $innerFieldsContainer
            .find('input:not(.address-lookup-disabled), select:not(.address-lookup-disabled)');
        var isHidden = !isVisible;

        $field.toggleClass('hidden', isHidden);
        $fieldControls.toggleClass('hidden', isHidden);
        $innerFieldsContainer.toggleClass('hidden', isHidden);

        if (isHidden) {
            // prevent input from being populated by form data collection
            $input.attr('disabled', 'disabled');
            $allInnerFieldsInputs.attr('disabled', 'disabled');
        } else {
            $input.removeAttr('disabled');
            $notDisabledInnerFieldsInputs.removeAttr('disabled');
        }
    };

    module.exports = Form;
});
